import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useLocation } from "react-router-dom";
// @mui
import { styled } from "@mui/material/styles";
import { Box, Fab, IconButton, Stack, Tooltip } from "@mui/material";
// hooks
import useSettings from "../../hooks/useSettings";
import useResponsive from "../../hooks/useResponsive";
import useCollapseDrawer from "../../hooks/useCollapseDrawer";
// atoms
import registerWaterInTakePopUpAtom from "src/recoil/atoms/registerWaterInTakePopUpAtom";
import userAtom from "src/recoil/atoms/userAtom";
// config
import { HEADER, NAVBAR } from "../../config";
//
import DashboardHeader from "./header";
import NavbarVertical from "./navbar/NavbarVertical";
import MealDetailsPopUp from "src/components/MealDetailsPopUp";
import RegisterWaterInTakePopUp from "src/components/RegisterWaterInTakePopUp";
import Iconify from "src/components/Iconify";
import useLocales from "src/hooks/useLocales";
import SnacksNoticePopUp from "src/components/SnacksNoticePopUp";
import clientFormRefillStatusAtom from "src/recoil/atoms/clientFormRefillStatusAtom";
import FreezePopUp from "src/components/FreezePopUp";
import TutorialPopUp from "src/components/TutorialPopUp";
// assets
import ramadanTheme from "../../assets/ramadanTheme.png";
import eidTheme from "../../assets/mosque.png";
// __apis__
import { fetchAssignedFollowUpsRequest } from "src/__apis__/followUp";

// ----------------------------------------------------------------------

const MainStyle = styled("main", {
  shouldForwardProp: (prop) => prop !== "collapseClick",
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  position: "relative",
  zIndex: 10,
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up("lg")]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create("margin-left", {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
  const { collapseClick, isCollapse } = useCollapseDrawer();

  const { themeLayout } = useSettings();

  const isDesktop = useResponsive("up", "lg");

  const [open, setOpen] = useState(false);

  const verticalLayout = themeLayout === "vertical";

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const triggerRegisterWaterInTakePopUp = useSetRecoilState(
    registerWaterInTakePopUpAtom
  );

  const { translate, currentLang } = useLocales();

  const refillFormStatus = useRecoilValue(clientFormRefillStatusAtom);

  const user = useRecoilValue(userAtom);

  const [agentPhoneNumber, setAgentPhoneNumber] = useState("");

  const fetchAssignedFollowUps = useCallback(async () => {
    await fetchAssignedFollowUpsRequest()
      .then((response) => {
        setAgentPhoneNumber(response[0].agent_phone_number);
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, []);

  useEffect(() => {
    fetchAssignedFollowUps();
  }, []);

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader
          onOpenSidebar={() => setOpen(true)}
          verticalLayout={verticalLayout}
        />

        <NavbarVertical />

        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { lg: "flex" },
        // minHeight: { lg: 1 },
        // position: "relative", // Needed for absolute positioning
        // "&::before": {
        // content: '""',
        // position: "absolute",
        // width: "100%",
        // height: "100%",
        // backgroundImage: `url(${eidTheme})`,
        // backgroundSize: "cover",
        // backgroundPosition: "center",
        // backgroundRepeat: "no-repeat",
        // zIndex: 1,
        // },
      }}
    >
      <DashboardHeader
        isCollapse={isCollapse}
        onOpenSidebar={() => setOpen(true)}
      />

      <NavbarVertical
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
      />

      <MainStyle collapseClick={collapseClick}>
        <Outlet />
        {/* Trigger register water in take pop up */}

        <Box
          sx={{
            position: "sticky",
            float: "right",
            bottom: "20px",
            right: "20px",
            display: "flex",
            flexDirection: "column",
            zIndex: 10000,
          }}
        >
          {/* Renew form pop up */}
          {!["/form/custom-program"].includes(pathname) && refillFormStatus && (
            <Fab
              onClick={() => navigate("/form/custom-program")}
              sx={{ mb: 1 }}
              variant="extended"
              color="error"
            >
              <Iconify sx={{ width: 30, ml: 1, height: 30 }} icon="bx:error" />
              {translate(
                "componentsTranslations.dashboardTranslations.renewProgramButton"
              )}
            </Fab>
          )}
          {[
            "/support/request-support",
            "/support/bug-report",
            "/support/inquires",
            "/overview/workout",
            "/overview/nutrition-plan",
          ].includes(pathname) && (
            <Fab
              variant="extended"
              onClick={() => triggerRegisterWaterInTakePopUp(true)}
            >
              <Iconify
                sx={{ width: 25, ml: 1, height: 25 }}
                icon="line-md:beer-loop"
              />
              {translate(
                "componentsTranslations.dashboardTranslations.waterInTake"
              )}
            </Fab>
          )}
        </Box>
        <Box
          sx={{
            position: "sticky",
            float: "right",
            bottom: "20px",
            right: "20px",
            display: "flex",
            flexDirection: "column",
            zIndex: 10000,
          }}
        >
          {(user?.follow_up_package === "mega-package" ||
            user?.follow_up_package === "golden-package") && (
            <Fab sx={{ mx: 1 }}>
              <Tooltip
                title={
                  currentLang.value === "ar"
                    ? "تحدث مع مدربك"
                    : "Talk to your coach"
                }
              >
                <IconButton
                  onClick={() => {
                    const phoneNumber = agentPhoneNumber.replace(/\D/g, ""); // remove spaces, +, etc
                    window.open(`https://wa.me/${phoneNumber}`, "_blank");
                  }}
                >
                  <Iconify
                    sx={{ width: 30, height: 30 }}
                    icon="logos:whatsapp-icon"
                  />
                </IconButton>
              </Tooltip>
            </Fab>
          )}
        </Box>
      </MainStyle>
      {/* Meal details pop up */}
      <MealDetailsPopUp />
      {/* Register water in take pop up */}
      <RegisterWaterInTakePopUp />
      {/* Snacks notice pop up */}

      <SnacksNoticePopUp />
      {/* Freeze popup */}
      <FreezePopUp />
      {/* Tutorial pop up */}
      <TutorialPopUp />
    </Box>
  );
}
